"use client";

import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import { TextStyle } from "@tiptap/extension-text-style";
import Color from "@tiptap/extension-color";
import TextAlign from "@tiptap/extension-text-align";
import { TiptapToolbar } from "./tiptap-toolbar";
import { useCallback, useEffect, useRef } from "react";
import type { JSONContent } from "@tiptap/react";

export interface VariableChip {
  key: string;
  label: string;
}

interface TiptapEditorProps {
  /** Contenu initial (Tiptap JSON). Ignoré après le premier rendu. */
  initialContent?: JSONContent;
  /** Appelé quand le contenu change (debounced côté parent si nécessaire) */
  onChange?: (content: JSONContent) => void;
  /** Placeholder quand l'éditeur est vide */
  placeholder?: string;
  /** Hauteur minimum de la zone d'édition */
  minHeight?: string;
  /** Chips de variables dynamiques affichés sous l'éditeur (clic = insertion au curseur) */
  variableChips?: VariableChip[];
}

export function TiptapEditor({
  initialContent,
  onChange,
  placeholder,
  minHeight = "200px",
  variableChips,
}: TiptapEditorProps) {
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        // StarterKit (v3) inclut déjà bold, italic, underline, heading, listes, etc.
        // → ne PAS rajouter @tiptap/extension-underline (doublon = warning Tiptap).
        heading: { levels: [1, 2, 3] },
      }),
      TextStyle,
      Color,
      TextAlign.configure({
        types: ["heading", "paragraph"],
      }),
    ],
    content: initialContent ?? "",
    editorProps: {
      attributes: {
        class: [
          "prose prose-sm max-w-none focus:outline-none",
          "px-4 py-3 bg-background rounded-b-lg border border-border border-t-0",
        ].join(" "),
        style: `min-height: ${minHeight}`,
        "data-placeholder": placeholder ?? "",
      },
    },
    onUpdate: ({ editor: e }) => {
      onChangeRef.current?.(e.getJSON());
    },
  });

  // Initialise le contenu UNE SEULE FOIS, à la première arrivée de
  // initialContent non vide. Ensuite, on ne resette plus l'éditeur :
  // l'état interne de Tiptap est la source de vérité pendant l'édition.
  //
  // Avant : la comparaison `initialContent !== initialContentRef.current`
  // déclenchait `setContent` à chaque re-render du parent (useLiveQuery
  // émet un nouvel objet rapport après chaque save → nouvelle référence
  // pour conclusionsText/methodologieText/etc.). Tiptap réinitialisait
  // alors le contenu et plaçait le curseur en fin de document (exact
  // bug remonté par Renaud : curseur saute en bas après ~1s = debounce
  // save + round-trip Dexie + emit liveQuery).
  //
  // Pour charger un rapport différent, le parent doit remonter le composant
  // via un prop `key` (changement de routeId par ex.) : l'init s'exécutera
  // de nouveau sur le nouvel instance.
  const initializedRef = useRef(false);
  useEffect(() => {
    if (!editor) return;
    if (!initializedRef.current && initialContent) {
      initializedRef.current = true;
      editor.commands.setContent(initialContent);
    }
  }, [editor, initialContent]);

  // Fermer le color picker quand on clique ailleurs
  const handleClickOutside = useCallback(() => {
    // Le color picker se ferme via son propre état dans la toolbar
  }, []);

  return (
    <div className="tiptap-wrapper" onClick={handleClickOutside}>
      <TiptapToolbar editor={editor} />
      <EditorContent editor={editor} />
      {variableChips && variableChips.length > 0 && (
        <div className="flex flex-wrap gap-1.5 px-3 py-2 border border-border border-t-0 rounded-b-lg bg-muted/30">
          <span className="text-xs text-muted-foreground self-center pr-1">Variables :</span>
          {variableChips.map((chip) => (
            <button
              key={chip.key}
              type="button"
              onClick={() => editor?.chain().focus().insertContent(`{{${chip.key}}}`).run()}
              className="inline-flex items-center px-2 py-0.5 rounded text-xs font-mono bg-muted hover:bg-accent hover:text-accent-foreground border border-border transition-colors"
              title={`Insérer {{${chip.key}}}`}
            >
              {chip.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export type { JSONContent };
